import { isEmpty, uniqBy } from "lodash"
import React, { MouseEventHandler, useEffect, useState } from "react"
import { Button, Col, Container, Dropdown, Form, Modal, Row, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import EditIcon from "../../assets/edit.svg"
import TrashIcon from "../../assets/trash.svg"
import { CHANGE_TYPE, SERVICE_TYPE } from "../../constants/constants"
import ArrowDown from "./ArrowDown.svg"
import ArrowDownFilled from "./ArrowDownFilled.svg"
import ArrowUp from "./ArrowUp.svg"
import ArrowUpFilled from "./ArrowUpFilled.svg"
import CreateOrUpdateSurchrage from "./CreateOrUpdateSurchrage"

const BulkUpdateModal = ({
    loading,
    show,
    setShow,
    handleClose,
    shippingRates,
    handleSubmit
}: {
    loading: boolean
    show: string
    setShow: Function
    handleClose: Function
    shippingRates: any[]
    handleSubmit: Function
}) => {
    const { t } = useTranslation()
    const [baseChargeChange, setBaseChargeChange] = useState({} as Record<string, any>)
    const [isInvalid, setIsInValid] = useState([] as string[])
    const [surchargeData, setSurchargeData] = useState({} as Record<string, any>)
    const [error, setError] = useState("")
    const [surcharges, setSurcharges] = useState([] as Record<string, any>[])
    const [addedSurcharges, setAddedSurcharges] = useState([] as Record<string, any>[])

    useEffect(() => {
        let tempSurcharges = [].concat.apply(
            [],
            shippingRates
                ?.filter((sr) => sr?.WeightRateSurcharges?.length > 0)
                ?.map((sr) => sr?.WeightRateSurcharges)
        ) as Record<string, any>[]
        tempSurcharges = tempSurcharges?.map((ts: Record<string, any>) => ({
            ...ts,
            default_value: 0,
            type: SERVICE_TYPE["0"],
            action_type: CHANGE_TYPE["0"]
        }))
        setSurcharges([...uniqBy(tempSurcharges, "shipping_surcharge_id")])
    }, [shippingRates?.length])

    return (
        <Modal
            size={"lg"}
            show={show === "bulkUpdateModal"}
            onHide={() => {
                setShow("")
            }}
            backdrop="static"
            keyboard={false}
            id="bulk-update-rates">
            <Modal.Header closeButton>
                <Modal.Title className={"pb-0"}>{t("Update Shipping Rates")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container>
                    <Row className="justify-content-around align-items-center grey-text mb-2">
                        <Col lg={4} xs={4}>
                            {t("Base Charge Change")}
                        </Col>
                        <Col lg={4} xs={4}>
                            {t("Rate type")}
                        </Col>
                        <Col lg={4} xs={4}>
                            {t("Increase/Decrease")}
                        </Col>
                    </Row>
                    <Row className="justify-content-around align-items-center">
                        <Col lg={4} xs={4} className={"text-center"}>
                            <Form.Control
                                name="base_charge_amount"
                                value={baseChargeChange?.base_charge_amount || 0}
                                type={"number"}
                                onChange={(e: any) => {
                                    setBaseChargeChange({
                                        ...baseChargeChange,
                                        [e.target.name]: parseFloat(e.target.value) || 0,
                                        action_type: baseChargeChange?.action_type
                                            ? baseChargeChange?.action_type
                                            : SERVICE_TYPE["1"]
                                    })
                                    if (parseFloat(e.target.value) <= 0) {
                                        setIsInValid([...isInvalid, e.target.name])
                                    } else {
                                        setIsInValid(isInvalid?.filter((i) => i !== "base_charge_amount"))
                                    }
                                }}
                                isInvalid={isInvalid?.includes("base_charge_amount")}
                            />
                            {isInvalid?.includes("base_charge_amount") && (
                                <Form.Control.Feedback>{t("Invalid Value Entered")}</Form.Control.Feedback>
                            )}
                        </Col>
                        <Col lg={4} xs={4} className="d-flex align-items-center justify-content-start">
                            <Form.Check
                                type="radio"
                                name="action_type"
                                label={t("Flat")}
                                checked={baseChargeChange?.action_type === SERVICE_TYPE["0"]}
                                onChange={(e: any) => {
                                    setBaseChargeChange({
                                        ...baseChargeChange,
                                        [e.target.name]: SERVICE_TYPE["0"]
                                    })
                                }}
                                required={true}
                            />
                            <Form.Check
                                type="radio"
                                name="action_type"
                                label={t("Percentage")}
                                checked={baseChargeChange?.action_type === SERVICE_TYPE["1"]}
                                onChange={(e: any) => {
                                    setBaseChargeChange({
                                        ...baseChargeChange,
                                        [e.target.name]: SERVICE_TYPE["1"]
                                    })
                                }}
                                required={true}
                                className="mx-4"
                            />
                        </Col>
                        <Col lg={4} xs={4} className={"text-center"}>
                            <div className="border rounded d-flex p-1 w-fit-content">
                                <img
                                    src={
                                        baseChargeChange?.action === CHANGE_TYPE["0"] ? ArrowUpFilled : ArrowUp
                                    }
                                    className={"rounded tr-pointer"}
                                    onClick={() =>
                                        setBaseChargeChange({ ...baseChargeChange, action: CHANGE_TYPE["0"] })
                                    }
                                />
                                &nbsp;
                                <img
                                    src={
                                        baseChargeChange?.action === CHANGE_TYPE["1"]
                                            ? ArrowDownFilled
                                            : ArrowDown
                                    }
                                    className={"rounded tr-pointer"}
                                    onClick={() =>
                                        setBaseChargeChange({ ...baseChargeChange, action: CHANGE_TYPE["1"] })
                                    }
                                />
                            </div>
                        </Col>
                    </Row>
                    {addedSurcharges?.length > 0 && (
                        <div className="table-responsive mt-3">
                            <Table>
                                <thead className="custom-thead border-bottom-0">
                                    <tr className="border rounded">
                                        <th className="col-3">Surcharge</th>
                                        <th className="col-3">Value</th>
                                        <th className="col-2">Flat/%</th>
                                        <th className="col-2">Inc/Dec</th>
                                        <th className="col-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="text-start custom-tbody">
                                    {addedSurcharges?.map((surcharge: Record<string, any>) =>
                                        !isEmpty(surchargeData) && surchargeData?.id === surcharge?.id ? (
                                            <CreateOrUpdateSurchrage
                                                surchargeData={surchargeData}
                                                setSurchargeData={setSurchargeData}
                                                error={error}
                                                setError={setError}
                                                bulkUpdate={true}
                                                surcharges={addedSurcharges}
                                                setSurcharges={setAddedSurcharges}
                                            />
                                        ) : (
                                            <tr
                                                className="rounded justify-content-around"
                                                key={`${surcharge?.id}-surcharge-bulkUpdate`}>
                                                <td className="col-3">
                                                    <span>{surcharge?.name}</span>
                                                </td>
                                                <td className="col-3">
                                                    <span>{surcharge?.default_value}</span>
                                                </td>
                                                <td className="col-2">
                                                    <span>
                                                        {surcharge?.type === SERVICE_TYPE["0"] ? "$" : "%"}
                                                    </span>
                                                </td>
                                                <td className="col-2">
                                                    <span>
                                                        {surcharge?.action_type === CHANGE_TYPE["1"]
                                                            ? "Decrease"
                                                            : "Increase"}
                                                    </span>
                                                </td>
                                                <td className="col-2 d-flex justify-content-start">
                                                    <img
                                                        src={EditIcon}
                                                        onClick={() => {
                                                            setSurchargeData({ ...surcharge })
                                                        }}
                                                        className="tr-pointer"
                                                    />
                                                    <img
                                                        src={TrashIcon}
                                                        onClick={() => {
                                                            setAddedSurcharges(
                                                                addedSurcharges.filter(
                                                                    (as) => as?.id !== surcharge?.id
                                                                )
                                                            )
                                                        }}
                                                        className="tr-pointer ml-2"
                                                    />
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                    {surcharges?.length - addedSurcharges?.length > 0 && (
                        <Dropdown className="mt-2 actions-menu d-flex justify-content-end">
                            <Dropdown.Toggle className="dashed-border" size={"sm"}>
                                + {t("Surcharge")}
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                {surcharges
                                    ?.filter((s) => !addedSurcharges?.map((as) => as?.id).includes(s?.id))
                                    ?.map((s: Record<string, any>) => (
                                        <Dropdown.Item
                                            key={s?.id}
                                            onClick={() => {
                                                setAddedSurcharges([...addedSurcharges, s])
                                                setSurchargeData({ ...s })
                                            }}>
                                            {s?.name}
                                        </Dropdown.Item>
                                    ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose as MouseEventHandler<HTMLButtonElement>}>
                    {t("Close")}
                </Button>

                <Button
                    onClick={() => {
                        const tempAddedSurcharges = addedSurcharges?.map(
                            (addedSurcharge: Record<string, any>) => ({
                                shipping_surcharge_id: addedSurcharge?.id,
                                action: addedSurcharge?.action_type,
                                action_type: addedSurcharge?.type,
                                amount: addedSurcharge?.default_value
                            })
                        )
                        handleSubmit({
                            ...baseChargeChange,
                            surcharges: [...tempAddedSurcharges]
                        })
                        handleClose()
                    }}
                    disabled={
                        error !== "" ||
                        baseChargeChange?.amount === 0 ||
                        baseChargeChange?.action === "" ||
                        !baseChargeChange?.action
                    }
                    className="btn btn-primary"
                    type="submit">
                    {t("Update")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default BulkUpdateModal
